/**
 * The SE Candidate Panel Scorecard rubric, encoded as data.
 *
 * This is the single source of truth for the whole app: the frontend renders
 * the form from it, and the backend validates submissions and computes the
 * weighted aggregate from it. Transcribed verbatim from the panel scorecard
 * PDF ("Value Framework Alignment — Final Panel: Intro + POV Playback").
 *
 * Category weights sum to 100. Each signal is scored 1–5. Category scores and
 * the overall score are the panelist's holistic 1–5 per category; the weighted
 * overall is the weight-weighted mean of the category scores, reported out of 5.
 */
export const SCALE = [
    { value: 1, label: "Did not demonstrate" },
    { value: 2, label: "Weak/inconsistent" },
    { value: 3, label: "Solid" },
    { value: 4, label: "Strong" },
    { value: 5, label: "Exceptional, teach-others-level" },
];
export const RECOMMENDATIONS = [
    { value: "strong_hire", label: "Strong hire" },
    { value: "hire", label: "Hire" },
    { value: "lean_no", label: "Lean no" },
    { value: "no", label: "No" },
];
export const CATEGORIES = [
    {
        id: "intro",
        ordinal: 0,
        title: "Introduction & Candidate Narrative",
        weight: 10,
        signals: [
            { id: "concise", text: "Clear, concise intro — respects time, doesn't run long into the POV slot" },
            { id: "motivation", text: 'Articulates genuine motivation for the SE role (not generic "I like tech and people")' },
            { id: "background", text: "Connects their own background to something value-driver-relevant (e.g., release risk, AI adoption, engineering toil)" },
            { id: "credible", text: "Comes across as confident and credible without overselling" },
        ],
    },
    {
        id: "discovery",
        ordinal: 1,
        title: "ABC Company Setup & Discovery Framing",
        weight: 20,
        note: 'No live customer to interview — "discovery" shows up in how well the candidate invents and frames ABC Company\'s challenges before showing solution.',
        signals: [
            { id: "narrative", text: 'Builds a specific, plausible ABC Company narrative (industry, team, current state) rather than a generic "company that needs feature flags"' },
            { id: "business-impact", text: "Frames ABC's challenges in business-impact terms (revenue, risk, speed, cost) before jumping to capability" },
            { id: "value-driver", text: "Explicitly anchors ABC's pain to one or more of the three value drivers" },
            { id: "so-what", text: 'Poses the kind of second-order "so what" question a real discovery call would surface, even in a monologue' },
            { id: "trap-setting", text: "Uses trap-setting structure (set context → open tension → close on implication) rather than just asserting pain" },
        ],
        redFlags: [
            { id: "features-first", text: "Jumps straight to features with no invented context" },
            { id: "vague-pain", text: "Pain is vague/generic" },
            { id: "no-value-driver", text: "Never ties pain to a value driver" },
        ],
    },
    {
        id: "pov",
        ordinal: 2,
        title: "POV Playback — RELEASE → OBSERVE → RECOVER → ITERATE → GOVERN",
        weight: 30,
        signals: [
            { id: "five-stage-arc", text: "Explicitly narrates the five-stage arc — not just a lab click-through" },
            { id: "release-kpi", text: "RELEASE tied to a KPI (deployment frequency, change failure rate, LTTV)" },
            { id: "observe-kpi", text: "OBSERVE tied to a KPI (latency/error rate, AI quality/drift, user impact)" },
            { id: "recover-kpi", text: "RECOVER tied to a KPI (MTTR, reduction in blast radius) — lands as the emotional high point" },
            { id: "iterate-kpi", text: "ITERATE tied to a KPI (experiment velocity, revenue/conversion lift)" },
            { id: "govern-kpi", text: "GOVERN tied to a KPI (compliance success rate, dev hours saved)" },
            { id: "connected-to-abc", text: "Every lab moment shown is explicitly connected back to an ABC Company challenge invented earlier — not a generic feature tour" },
            { id: "ai-control-plane", text: "If AI in scope: positions LaunchDarkly as the control plane, not the model — safety, evaluation, iteration velocity" },
        ],
        redFlags: [
            { id: "disconnected-tour", text: "Feature tour disconnected from ABC's story" },
            { id: "no-metrics", text: "No metric tie-ins" },
            { id: "recover-afterthought", text: "RECOVER treated as an afterthought" },
        ],
    },
    {
        id: "value-fluency",
        ordinal: 3,
        title: "Value Fluency & Differentiator Use",
        weight: 15,
        signals: [
            { id: "differentiators", text: "Uses approved differentiators naturally in context (not name-dropped)" },
            { id: "proof-point", text: "Selects a proof point that matches the scale/situation invented for ABC, not a generic logo-drop" },
            { id: "directional-metrics", text: "Frames metrics as directional hypotheses tied to ABC's own (invented) success criteria — never guarantees" },
            { id: "redirects", text: "Redirects appropriately when panel pushes on pricing, roadmap, or competitive teardowns, without being dismissive" },
        ],
    },
    {
        id: "handling-room",
        ordinal: 4,
        title: "Handling the Room — Adaptability & Time Management",
        weight: 15,
        note: "This is a simulated live meeting — score how they handle being an SE in the room, not just their prepared content.",
        signals: [
            { id: "returns-to-value", text: "Handles panel questions/interruptions by returning to a value driver or differentiator rather than freezing or over-explaining" },
            { id: "adjusts-pacing", text: "Visibly adjusts pacing or content when the panel pushes back or redirects (doesn't just plow through a script)" },
            { id: "i-dont-know", text: 'Comfortable saying "I don\'t know, but here\'s how I\'d find out" rather than bluffing' },
            { id: "time-management", text: "Manages the 45-minute window well — doesn't run out of time before RECOVER/ITERATE/GOVERN, or pad early sections" },
            { id: "non-promotional", text: "Maintains outcome-oriented, non-promotional tone throughout" },
        ],
    },
    {
        id: "self-reflection",
        ordinal: 5,
        title: "Self-Reflection",
        weight: 10,
        signals: [
            { id: "accurate-strengths", text: "Self-identified strengths match what the panel actually observed (accurate self-awareness)" },
            { id: "specific-gaps", text: 'Self-identified gaps are specific and substantive, not generic ("I\'d tighten up timing" vs. "I think it went well")' },
            { id: "open-to-feedback", text: "Responds to panel feedback with curiosity/openness rather than defensiveness" },
        ],
    },
];
/** Free-text prompts at the bottom of the scorecard. */
export const FREE_TEXT_PROMPTS = {
    coachEveryone: "One thing this candidate does that we should coach every SE to do:",
    gapConcern: "One gap that would concern me in the first 90 days:",
};
export const WEIGHT_TOTAL = CATEGORIES.reduce((sum, c) => sum + c.weight, 0); // 100
export function getCategory(id) {
    return CATEGORIES.find((c) => c.id === id);
}
